import { useEffect, useRef, useState } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { X, Upload, FileText, Image, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogMain,
  DialogFooter,
  DialogTitle,
  StarRating,
} from '@ncoder/ui';
import { cn } from '@/lib/utils';
import { useCreateUserFeedback } from '@/hooks/feedback/useCreateUserFeedback';
import { useCreateBugReport, type BugReportAttachment } from '@/hooks/feedback/useCreateBugReport';
import {
  ACCEPTED_FILE_EXTENSIONS,
  IMAGE_FILE_EXTENSIONS,
  MAX_ATTACHMENT_MB,
} from './feedback.constants';

// ── Types ─────────────────────────────────────────────────────────────────────

type Tab = 'rate' | 'bug';

export interface FeedbackFormData {
  q1: number; q2: number; q3: number; q4: number; q5: number;
}

interface BugFormData { headline: string; comment: string; }

const feedbackDefaults: FeedbackFormData = { q1: 0, q2: 0, q3: 0, q4: 0, q5: 0 };
const bugDefaults: BugFormData = { headline: '', comment: '' };

const FEEDBACK_QUESTIONS = [
  'How would you rate the Workflow experience?',
  'How would you rate the Agent configuration experience?',
  'How would you rate the Metadata explorer?',
  'How would you rate the overall experience?',
  'How likely are you to recommend us?',
] as const;

// ── File helpers ──────────────────────────────────────────────────────────────

const ACCEPTED_EXTENSIONS = ACCEPTED_FILE_EXTENSIONS;
const IMAGE_EXTENSIONS = IMAGE_FILE_EXTENSIONS;
const MAX_MB = MAX_ATTACHMENT_MB;

interface AttachmentFile { name: string; size: number; base64: string; previewUrl?: string; }

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatBytes(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(name: string) {
  return IMAGE_EXTENSIONS.has(name.split('.').pop()?.toLowerCase() ?? '');
}

// ── Root ──────────────────────────────────────────────────────────────────────

export interface ReportBugFormData { headline: string; comment: string; }

interface Props { open: boolean; onClose: () => void; }

export function FeedbackModal({ open, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('rate');

  // ── Feedback tab state ──
  const { mutate: submitFeedback, isPending: submittingFeedback } = useCreateUserFeedback();
  const feedbackForm = useForm<FeedbackFormData>({ defaultValues: feedbackDefaults, mode: 'onChange' });
  const { formState: { isValid: feedbackValid }, reset: resetFeedback } = feedbackForm;

  // ── Bug tab state ──
  const { mutate: submitBug, isPending: submittingBug } = useCreateBugReport();
  const bugForm = useForm<BugFormData>({ defaultValues: bugDefaults, mode: 'onChange' });
  const { formState: { isValid: bugValid }, reset: resetBug } = bugForm;

  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [fileError, setFileError]     = useState<string | null>(null);
  const [isDragging, setIsDragging]   = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isPending = submittingFeedback || submittingBug;

  useEffect(() => {
    if (open) {
      setTab('rate');
      resetFeedback(feedbackDefaults);
      resetBug(bugDefaults);
      setAttachments([]);
      setFileError(null);
    }
  }, [open, resetFeedback, resetBug]);

  const handleClose = () => {
    resetFeedback(feedbackDefaults);
    resetBug(bugDefaults);
    setAttachments([]);
    setFileError(null);
    onClose();
  };

  // ── File handling ──
  const processFiles = async (files: File[]) => {
    setFileError(null);
    const oversized = files.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (oversized) { setFileError(`"${oversized.name}" exceeds the ${MAX_MB} MB limit.`); return; }
    const converted = await Promise.all(files.map(async (f) => ({
      name: f.name, size: f.size,
      base64: await fileToBase64(f),
      previewUrl: isImage(f.name) ? URL.createObjectURL(f) : undefined,
    })));
    setAttachments((prev) => [...prev, ...converted]);
  };

  const removeAttachment = (i: number) => {
    setAttachments((prev) => {
      const removed = prev[i];
      if (removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, j) => j !== i);
    });
  };

  // ── Submit ──
  const handleSubmit = () => {
    if (tab === 'rate') {
      feedbackForm.handleSubmit((data) => {
        submitFeedback(
          { workflowRating: data.q1, agentConfigRating: data.q2, metadataExplorerRating: data.q3, overallRating: data.q4, recommendRating: data.q5 },
          { onSuccess: handleClose },
        );
      })();
    } else {
      bugForm.handleSubmit((data) => {
        const payload: BugReportAttachment[] = attachments.map((a) => ({ filename: a.name, content: a.base64 }));
        submitBug({ summary: data.headline, description: data.comment, attachments: payload }, { onSuccess: handleClose });
      })();
    }
  };

  const canSubmit = tab === 'rate' ? feedbackValid : bugValid;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Share feedback</DialogTitle>
          <DialogDescription className="text-xs">
            Help us improve — rate your experience or report something that isn't working.
          </DialogDescription>
        </DialogHeader>

        {/* Tab switcher */}
        <div className="px-6 pt-1">
          <div className="flex rounded-lg bg-muted/50 border border-border p-0.5 gap-0.5">
            {([['rate', 'Rate experience'], ['bug', 'Report a bug']] as const).map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setTab(v)}
                className={cn(
                  "flex-1 py-1.5 text-xs font-medium rounded-md transition-colors",
                  tab === v
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <DialogMain className="space-y-3">
          {tab === 'rate' ? (
            <FormProvider {...feedbackForm}>
              {FEEDBACK_QUESTIONS.map((question, i) => (
                <div key={i} className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">{question}</label>
                  <Controller
                    name={`q${(i + 1) as 1 | 2 | 3 | 4 | 5}`}
                    rules={{ required: true, min: 1, max: 5 }}
                    render={({ field }) => (
                      <StarRating value={field.value} onChange={field.onChange} disabled={isPending} />
                    )}
                  />
                </div>
              ))}
            </FormProvider>
          ) : (
            <FormProvider {...bugForm}>
              <div className="space-y-1.5">
                <label htmlFor="bug-headline" className="text-xs font-medium text-foreground">Headline</label>
                <input
                  id="bug-headline"
                  placeholder="Brief summary of the bug"
                  {...bugForm.register('headline', { required: true })}
                  disabled={isPending}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="bug-comment" className="text-xs font-medium text-foreground">Comment</label>
                <textarea
                  id="bug-comment"
                  placeholder="Steps to reproduce, what you expected, etc."
                  rows={4}
                  {...bugForm.register('comment', { required: true })}
                  disabled={isPending}
                  className="w-full px-3 py-2 text-xs rounded-lg border border-border bg-muted/30 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary resize-none disabled:opacity-50"
                />
              </div>

              {/* Upload zone */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-foreground">
                  Attachments <span className="text-muted-foreground font-normal">— optional, max {MAX_MB} MB each</span>
                </label>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => !isPending && fileInputRef.current?.click()}
                  onKeyDown={(e) => e.key === 'Enter' && !isPending && fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={async (e) => { e.preventDefault(); setIsDragging(false); await processFiles(Array.from(e.dataTransfer.files)); }}
                  className={cn(
                    'flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-4 text-center transition-colors cursor-pointer',
                    isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/40',
                    isPending && 'pointer-events-none opacity-50',
                  )}
                >
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-[11px] text-muted-foreground/70">PNG, JPG, PDF, TXT, CSV, ZIP…</p>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_EXTENSIONS}
                  className="hidden"
                  onChange={async (e) => { const files = Array.from(e.target.files ?? []); if (files.length) await processFiles(files); e.target.value = ''; }}
                  disabled={isPending}
                />

                {fileError && <p className="text-xs text-destructive">{fileError}</p>}

                {attachments.length > 0 && (
                  <ul className="space-y-1.5">
                    {attachments.map((file, i) => (
                      <li key={i} className="flex items-center gap-2.5 rounded-lg border border-border/60 bg-muted/20 px-3 py-1.5">
                        {file.previewUrl
                          ? <img src={file.previewUrl} alt={file.name} className="h-6 w-6 rounded object-cover shrink-0" />
                          : <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted">
                              {isImage(file.name) ? <Image className="h-3.5 w-3.5 text-muted-foreground" /> : <FileText className="h-3.5 w-3.5 text-muted-foreground" />}
                            </div>
                        }
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-xs font-medium">{file.name}</p>
                          <p className="text-[10px] text-muted-foreground">{formatBytes(file.size)}</p>
                        </div>
                        <button type="button" onClick={() => removeAttachment(i)} disabled={isPending} className="shrink-0 p-1 rounded text-muted-foreground hover:text-destructive transition-colors disabled:pointer-events-none">
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </FormProvider>
          )}
        </DialogMain>

        <DialogFooter>
          <button type="button" onClick={handleClose} disabled={isPending} className="px-3 py-1.5 text-xs rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit || isPending} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:pointer-events-none">
            {isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            {isPending ? 'Submitting…' : tab === 'rate' ? 'Submit feedback' : 'Submit report'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Keep export alias so existing imports of ReportBugModal still work
export { FeedbackModal as ReportBugModal };
